import { apiRequest } from './api';

export type Organization = {
  id: number;
  name: string;
  title: string;
  subtitle?: string | null;
  logo?: string | null;
  watermark_image?: string | null;
  watermark_same_as_logo?: boolean;
  primary_color: string;
  secondary_color: string;
  email?: string | null;
  mobile?: string | null;
  address?: string | null;
  website?: string | null;
  status: 'active' | 'inactive';
  created_by?: number | null;
  created_by_name?: string | null;
  users_count?: number;
  created_at?: string;
  updated_at?: string;
};

export type OrganizationStats = {
  total: number;
  active: number;
  inactive: number;
};

export type OrganizationListParams = {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
  sort_by?: 'name' | 'title' | 'status' | 'created_at' | 'users_count';
  sort_dir?: 'asc' | 'desc';
};

export async function fetchOrganizations(params: OrganizationListParams = {}) {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.per_page) searchParams.set('per_page', String(params.per_page));
  if (params.search) searchParams.set('search', params.search);
  if (params.status && params.status !== 'All') searchParams.set('status', params.status);
  if (params.sort_by) searchParams.set('sort_by', params.sort_by);
  if (params.sort_dir) searchParams.set('sort_dir', params.sort_dir);

  const query = searchParams.toString();
  const endpoint = `/api/organizations${query ? `?${query}` : ''}`;

  return apiRequest<Organization[]>(endpoint);
}

export async function fetchOrganizationStats() {
  const result = await apiRequest<OrganizationStats>('/api/organizations/stats');
  return result.data || { total: 0, active: 0, inactive: 0 };
}

export async function fetchOrganizationLookup() {
  const result = await apiRequest<Organization[]>('/api/organizations/lookup');
  return result.data || [];
}

export async function fetchMyOrganizations() {
  const result = await apiRequest<Organization[]>('/api/organizations/my-organizations');
  return result.data || [];
}

export async function fetchOrganizationById(id: number | string) {
  const result = await apiRequest<Organization>(`/api/organizations/${id}`);
  return result.data;
}

export async function createOrganization(formData: FormData) {
  return apiRequest<Organization>('/api/organizations', {
    method: 'POST',
    body: formData,
  });
}

export async function updateOrganization(id: number | string, formData: FormData) {
  return apiRequest<Organization>(`/api/organizations/${id}`, {
    method: 'PUT',
    body: formData,
  });
}

export async function toggleOrganizationStatus(id: number | string, status: 'active' | 'inactive') {
  return apiRequest<Organization>(`/api/organizations/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}
